<template>
    <nav class="KbArticleList" :aria-labelledby="headingId">
        <h2 :id="headingId" class="KbArticleList__heading">
            {{ heading }}
        </h2>
        <ul class="KbArticleList__list">
            <li v-for="article in articles" :key="article.id" class="KbArticleList__item">
                <span
                    v-if="article.id === currentArticleId"
                    class="KbArticleList__row KbArticleList__row--current"
                    aria-current="page"
                >
                    <EidIcon class="KbArticleList__icon" :icon-src="faFileLines" />
                    <span class="KbArticleList__title">{{ article.title }}</span>
                </span>
                <KbLink
                    v-else
                    class="KbArticleList__row KbArticleList__row--link"
                    :target="{ kind: 'article', id: article.id }"
                >
                    <EidIcon class="KbArticleList__icon" :icon-src="faFileLines" />
                    <span class="KbArticleList__title">{{ article.title }}</span>
                    <EidIcon class="KbArticleList__chevron" :icon-src="faChevronRight" />
                </KbLink>
            </li>
        </ul>
    </nav>
</template>

<style lang="scss">
.KbArticleList {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &__heading {
        margin: 0;
        font-size: var(--text-section-header-size, 16px);
        font-weight: 700;
        line-height: 1.3;
        color: var(--eidSecureBlue);
    }

    &__list {
        display: flex;
        flex-direction: column;
        gap: 2px;
        margin: 0;
        padding: 0;
        list-style: none;
    }

    &__row {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        margin: 0 -10px;
        border-radius: 6px;
        color: var(--eidSecureBlue-75);
        text-decoration: none;

        &--link {
            transition: background-color 0.15s ease, color 0.15s ease;

            &:hover {
                background-color: var(--eidTechGray-10);
                color: var(--eidSecureBlue);
            }

            &:hover .KbArticleList__chevron {
                opacity: 1;
                transform: translateX(0);
            }

            &:focus-visible {
                outline: none;
                box-shadow: 0 0 0 2px var(--eidTrustGreen);
            }
        }

        &--current {
            background-color: var(--eidTechGray-10);
            color: var(--eidSecureBlue);
            font-weight: 700;
        }
    }

    &__icon {
        flex-shrink: 0;
        font-size: 13px;
        color: var(--eidTechGray-75);
    }

    &__title {
        flex: 1;
        font-size: var(--text-body-size, 14px);
    }

    &__chevron {
        flex-shrink: 0;
        font-size: 11px;
        opacity: 0;
        transform: translateX(-4px);
        transition: opacity 0.15s ease, transform 0.15s ease;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faChevronRight, faFileLines } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { KbArticleStub } from '@evidentid/tprm-portal-lib/models/kb';

    import KbLink from '@/modules/kb/components/KbLink/KbLink.vue';

    export default defineComponent({
        name: 'KbArticleList',
        components: { EidIcon, KbLink },
        props: {
            heading: {
                type: String,
                required: true,
            },
            // Unique per instance on the page: the nav is labelled by this heading.
            headingId: {
                type: String,
                required: true,
            },
            articles: {
                type: Array as PropType<KbArticleStub[]>,
                required: true,
            },
            currentArticleId: {
                type: String,
                default: undefined,
            },
        },
        data: () => ({ faChevronRight, faFileLines }),
    });
</script>
